import SignalRConnection from "./utils/SignalRConnection";
import { errorPrompt, okCancel, prompt, yesNo } from "./Control/Vue/Notifications/Prompts";
import axios from "axios";
import { defineStore } from "pinia";
import gsap from "gsap";

// Non-vue (jQuery) components.
import { Carousel } from "./controls/Carousel";
import { CodeInputGroup } from "./controls/membership/CodeInputGroup";
import { LoginFormChildren } from "./controls/LoginFormChildren";
import { MemberList } from "./lists/MemberList";
import { ModerationNotifier } from "./controls/ModerationNotifier";
import { Navbar } from "./controls/Navbar";
import { NewspaperEditor } from "./controls/NewspaperEditor";
import { PasswordInputGroup } from "./controls/form/PasswordInputGroup";
import { ProductSelector } from "./controls/membership/ProductSelector";
import { ProfileForm } from "./controls/ProfileForm";
import { PurchaseSummary } from "./controls/membership/PurchaseSummary";
import { SelectControlledControls } from "./webcontrols/SelectControlledControls";
import { SubjectSelectorPriceInformation } from "./controls/membership/SubjectSelectorPriceInformation";
import { StepWizard } from "./controls/step-wizard/StepWizard";

const legacyControls = [
	ActivePlazasList,
	ActiveRatingBar,
	Animation,
	AssessmentMode,
	AwardScoops,
	BusinessSimulation,
	ChangeableMiniMeItem,
	CloseButton,
	Collapser,
	ColorableSvgItem,
	ColorSelector,
	ControlSwitcher,
	DeleteMessageDesign,
	DesignstudioEditor,
	DOMAttached,
	DraggableSvgItem,
	DragonChoose,
	DragonWorld,
	EasierLevel,
	EnhancedButton,
	ExternalHyperLink,
	FeatureLevelSelector,
	FeedComment,
	FeedLoader,
	FeedStatusMessage,
	FocusFirstInput,
	FreeInputQuiz,
	FriendshipRequest,
	FriendshipRequestButton,
	GameAnimationContainer,
	GameList,
	GameLoadTabAndPopupButton,
	GamePopup,
	GamePopupContainer,
	GamesFlashlessGeography,
	GamesFlashQuiz,
	GamesHangman,
	GamesLeveledFlashQuiz,
	GamesMathPuzzle,
	GamesMultiplayerConstruction,
	GamesMultiplayerQuiz,
	GamesMultiplayerQuizListItem,
	GamesQuiz,
	GamesRace,
	GamesRaceOpponent,
	GamesSearchBox,
	GamesTextToSpeech,
	GenericSounds,
	GNOMActionTimer,
	GNOMList,
	GNOMListItem,
	GNOMPetImageBox,
	GNOMRefreshingStatBox,
	GNOMStatBox,
	HideableControl,
	IFrame,
	InventoryList,
	Invitation,
	InvitationMethod,
	JSTabSelector,
	LegacyGameHeader,
	LevelMedia,
	Levels,
	ListBase,
	MemberSelector,
	MessageSend,
	MiniMeAccessory,
	MiniMeAccessoryManager,
	MiniMeBottom,
	MiniMeFaces,
	MiniMeHair,
	MiniMeMain,
	MiniMeShoes,
	MiniMeSpeechBubble,
	MiniMeTop,
	MiniWorldExitButton,
	MiniworldHouse,
	MoveAnimation,
	MultipleChoiceQuiz,
	NewsletterDesign,
	NewspaperAd,
	NewspaperEditionList,
	PetOwnerSelector,
	PlayPauseButton,
	PlazaList,
	PopupEmailPreview,
	PopupOpener,
	PostingRating,
	PostingVisibility,
	RegisterHelp,
	ReportWatchedVideoListener,
	RewardVideoListener,
	SaveableSVG,
	SearchBox,
	SharedList,
	ShootingPrototype,
	ShowcaseItem,
	SpellingFire,
	SuperDragonFire,
	TabbedApplication,
	TabPopupMenu,
	TextBox,
	TextResource,
	TextToSpeech,
	ThemedButton,
	ToggleFeedbackSound,
	ToggleQuestionReader,
	TypingGame,
	TypingGuardian,
	UpScaledImageHandler,
	UrlTemplate,
	VimeoPlayer,
	VisibilitySwitcher,
	Window,
];

export async function enableLegacyControls() {
	window.gsap = gsap;
	window.useStore = (id: string) => defineStore(id, () => {
		throw new Error(`Pinia Store ID:${id} doesn't exist.`);
	})();
	window.MiaOkCancel = okCancel;
	window.MiaYesNo = yesNo;
	window.MiaPrompt = prompt;
	window.MiaErrorPrompt = errorPrompt;
	window.SignalRConnection = SignalRConnection;
	window.axios = axios;

	// These controls are manually registered instead of iterating over them because their names must be preserved as-is.
	// Our production minifier (JavaScriptShipMinified) renames class identifiers like `Navbar` to short names like `XUZ`,
	// which breaks the string-based control resolution in legacyGlobal.DOMReadyConstruct.
	// Manual registration ensures the correct class is assigned to the expected string name before minification.
	window.legacyGlobal.getControlPromise("Carousel").resolve(Carousel);
	window.legacyGlobal.getControlPromise("CodeInputGroup").resolve(CodeInputGroup);
	window.legacyGlobal.getControlPromise("LoginFormChildren").resolve(LoginFormChildren);
	window.legacyGlobal.getControlPromise("MemberList").resolve(MemberList);
	window.legacyGlobal.getControlPromise("ModerationNotifier").resolve(ModerationNotifier);
	window.legacyGlobal.getControlPromise("Navbar").resolve(Navbar);
	window.legacyGlobal.getControlPromise("NewspaperEditor").resolve(NewspaperEditor);
	window.legacyGlobal.getControlPromise("PasswordInputGroup").resolve(PasswordInputGroup);
	window.legacyGlobal.getControlPromise("ProductSelector").resolve(ProductSelector);
	window.legacyGlobal.getControlPromise("ProfileForm").resolve(ProfileForm);
	window.legacyGlobal.getControlPromise("PurchaseSummary").resolve(PurchaseSummary);
	window.legacyGlobal.getControlPromise("SelectControlledControls").resolve(SelectControlledControls);
	window.legacyGlobal.getControlPromise("StepWizard").resolve(StepWizard);
	window.legacyGlobal.getControlPromise("SubjectSelectorPriceInformation").resolve(SubjectSelectorPriceInformation);

	legacyControls.forEach((control) => {
		window.legacyGlobal.getControlPromise(control.name).resolve(control);
	});
}
